import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, X, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";

/**
 * Landing page Revela — English version (rev. 2026-06-28).
 *
 * Tone: bold, direct, Silicon Valley pitch. Tighter than the Italian original.
 * Same visual identity (silver + white + gold, Cormorant Garamond display).
 * CTAs: "Request beta access" + "Join as a scientific partner".
 */
const LandingPageEN = () => {
  const navigate = useNavigate();
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [requestType, setRequestType] = useState(null); // "beta" | "partner" | null

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/auth/me");
        if (!cancelled && res?.data?.user_id) {
          navigate("/oggi", { replace: true });
          return;
        }
      } catch {
        // Not authenticated → stay on landing
      }
      if (!cancelled) setCheckingAuth(false);
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const openBetaForm = () => setRequestType("beta");
  const openPartnerForm = () => setRequestType("partner");
  const closeForm = () => setRequestType(null);

  if (checkingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" strokeWidth={1.5} />
      </div>
    );
  }

  const frameworks = [
    "Russell — Circumplex Model of Affect",
    "Kahneman — Day Reconstruction Method",
    "Csíkszentmihályi — Flow & Experience Sampling",
    "Karasek — Demand / Control Model",
    "Deci & Ryan — Self-Determination Theory",
    "Sonnentag — Recovery Experiences",
    "Bandura — Self-efficacy",
    "Bowlby — Attachment & meaningful bonds",
    "Seligman — Learned Helplessness",
    "Cacioppo — Perceived Loneliness",
    "Lewinsohn — Behavioral Activation",
    "Trull & Ebner-Priemer — Ecological Momentary Assessment",
  ];

  const clusters = [
    {
      cluster: "General wellbeing",
      items: [
        { title: "Daily wellbeing index", desc: "With 7-day and 30-day trends." },
        { title: "Weekly narrative recap", desc: "A natural-language summary of your week, written for you." },
        { title: "Emotional recovery speed", desc: "How fast you return to balance after a negative episode." },
      ],
    },
    {
      cluster: "Work & activities",
      items: [
        { title: "How you're experiencing work", desc: "Whether you're under pressure, in active challenge, at ease, or stuck — across every area of your life." },
        { title: "Effort-recovery balance", desc: "Whether your breaks are truly compensating your output." },
        { title: "Paradox of control", desc: "When feeling in control isn't enough to feel well." },
      ],
    },
    {
      cluster: "Motivation",
      subtitle: "Self-Determination Theory",
      items: [
        { title: "Autonomy index", desc: "How much your actions are genuinely chosen by you." },
        { title: "Competence index", desc: "How capable you feel in the situations that matter." },
        { title: "Connection index", desc: "Net nourishment from your close bonds." },
      ],
    },
    {
      cluster: "Affect & regulation",
      items: [
        { title: "Affect map", desc: "Flow · Serenity · Agitation · Apathy." },
        { title: "Within-day mood swings", desc: "How much your mood oscillates over a single day." },
        { title: "Emotional persistence", desc: "How long a feeling stays with you, from one moment to the next." },
        { title: "Affective resilience", desc: "Your capacity to hold up under heavy load." },
      ],
    },
    {
      cluster: "Relational dimension",
      items: [
        { title: "Asymmetry in relationships", desc: "Which bonds nourish you, which drain you." },
        { title: "Nourishing solitude vs heavy solitude", desc: "Same «being alone», opposite sign." },
        { title: "Contextual helplessness", desc: "Where you've stopped believing you can have an impact." },
      ],
    },
  ];

  const normativi = {
    cluster: "Normative comparisons",
    items: [
      { title: "Compared to yourself, over time", desc: "You are your own first benchmark." },
      { title: "Compared to international wellbeing norms", desc: "OECD Better Life Index, WHO-5, ESS social indicators." },
      { title: "Compared to the Revela community", desc: "Always anonymous and aggregated." },
    ],
  };

  const steps = [
    {
      n: "1",
      title: "Check in in under 25 seconds",
      body: "Whenever you want — or when the app gently pings you. Four short questions: where you are, who you're with, what you're doing, how you feel.",
    },
    {
      n: "2",
      title: "Let the patterns emerge",
      body: "First insights unlock after just a few check-ins. At 4 weeks you see reliable maps. At 3 months you see yourself with new clarity.",
    },
    {
      n: "3",
      title: "Try the suggestions that resonate",
      body: "As Revela learns about you, it surfaces small prompts tailored just for you. It's up to you whether — and how — to act on them.",
    },
  ];

  return (
    <div className="min-h-screen bg-white text-slate-800" data-testid="landing-page-revela-en">
      <LanguageToggle current="en" />

      {/* ===== HERO ===== */}
      <section className="border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 pt-16 sm:pt-24 pb-16 sm:pb-20">
          <div className="space-y-6">
            <div>
              <p className="font-display text-xl sm:text-2xl text-slate-500 italic font-light tracking-wide" data-testid="hero-hello-en">
                Hello
              </p>
              <h1 className="font-display text-6xl sm:text-7xl md:text-8xl text-gold leading-none mt-1" data-testid="hero-revela-en">
                Revela
              </h1>
            </div>

            <p className="font-display text-2xl sm:text-3xl md:text-4xl text-slate-600 italic font-light leading-snug max-w-2xl">
              Track your days. Discover your patterns.
            </p>

            <ul className="text-lg sm:text-xl text-slate-600 leading-relaxed font-light max-w-2xl space-y-2" data-testid="hero-promise-en">
              <li>Log your experience in <strong className="font-medium">under 25 seconds</strong>.</li>
              <li className="text-gold font-display italic text-2xl sm:text-3xl leading-snug pt-2">
                Discover how you actually function.
              </li>
              <li className="text-gold font-display italic text-2xl sm:text-3xl leading-snug">
                Receive suggestions tailored to you.
              </li>
              <li className="text-gold font-display italic text-2xl sm:text-3xl leading-snug">
                Improve the quality of your days, week after week.
              </li>
              <li className="text-slate-500 italic pt-2">The more data you collect, the clearer the patterns become — and the more precise the insights.</li>
            </ul>

            <div className="pt-3 flex flex-col sm:flex-row gap-3 items-stretch sm:items-start">
              <button
                onClick={openBetaForm}
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 text-white text-base font-light tracking-wide hover:shadow-lg transition-shadow"
                data-testid="hero-cta-beta-tester-en"
              >
                Request beta access
              </button>
              <button
                onClick={openPartnerForm}
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border border-amber-700/40 text-slate-700 text-base font-light tracking-wide hover:bg-amber-50/50 hover:border-amber-700/60 transition-colors"
                data-testid="hero-cta-partner-en"
              >
                Join as a scientific partner
              </button>
            </div>

            <p className="text-sm sm:text-base text-slate-600 font-light tracking-wide" data-testid="hero-already-registered-en">
              Already a member?{" "}
              <Link to="/login" className="text-slate-800 underline decoration-amber-700/40 underline-offset-2 hover:decoration-amber-700/80 transition-colors font-medium" data-testid="hero-already-registered-link-en">
                Sign in
              </Link>
            </p>

            <p className="text-sm text-slate-500 font-light tracking-wide pt-3 max-w-2xl" data-testid="hero-beta-note-en">
              🌱 Revela is in <strong className="font-medium text-slate-700">private beta</strong>, currently free of charge. As a beta tester you get immediate access to the app: log your check-ins and within a few days you start seeing your first behavioral patterns. Designed and built by <strong className="font-medium text-slate-700">Laura Piccinini</strong>, psychologist and psychotherapist ·{" "}
              <a
                href="https://www.laurapiccinini.it"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-600 hover:text-slate-800 underline decoration-slate-300 underline-offset-2"
              >
                laurapiccinini.it
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* ===== POSITIONING / WHY REVELA ===== */}
      <section className="border-b border-slate-200 bg-slate-50">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 py-16 sm:py-20" data-testid="why-revela-en">
          <p className="text-xs sm:text-sm text-gold font-medium tracking-[0.18em] uppercase mb-4 text-center">
            Why Revela
          </p>
          <p className="font-display text-3xl sm:text-4xl md:text-5xl text-slate-700 font-light leading-tight mb-6 text-center">
            Research-grade self-knowledge.<br/>Not just another wellness app.
          </p>
          <p className="text-base sm:text-lg text-slate-600 leading-relaxed font-light text-center max-w-2xl mx-auto">
            Most wellbeing apps sell you meditations, breathing exercises and gratitude prompts. Revela is different: it's the first consumer-grade <strong className="font-medium">Ecological Momentary Assessment</strong> platform — the same methodology used in academic research to study real experience in real time — designed by a psychotherapist for the people who need it most: <em>everyone</em>.
          </p>
        </div>
      </section>

      {/* ===== WHAT YOU'LL DISCOVER — INDICATORS ===== */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 py-16 sm:py-20">
          <p className="font-display text-xl sm:text-2xl md:text-3xl text-slate-600 italic font-light mb-4 text-center">
            What you'll discover, after a few weeks
          </p>
          <p className="text-base sm:text-lg text-slate-600 text-center max-w-2xl mx-auto mb-12 font-light leading-relaxed">
            Over <strong className="font-medium">70 wellbeing indicators</strong> and <strong className="font-medium">3,000 distinct experiential configurations</strong>, organized into <em>five families of insights</em> that unlock progressively.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="indicators-grid-en">
            {clusters.map((group, i) => {
              const isOrphan = i === clusters.length - 1 && clusters.length % 2 === 1;
              return (
                <div
                  key={i}
                  className={`bg-white rounded-lg p-6 sm:p-7 box-gold box-gold-accent hover:shadow-md transition-shadow ${isOrphan ? "md:col-span-2 md:max-w-[calc(50%-0.625rem)] md:mx-auto md:w-full" : ""}`}
                  data-testid={`cluster-en-${i}`}
                >
                  <p className="text-xs sm:text-sm text-gold font-medium tracking-[0.18em] uppercase mb-1">
                    {group.cluster}
                  </p>
                  {group.subtitle && (
                    <p className="font-display text-base text-slate-500 italic font-light mb-4">
                      {group.subtitle}
                    </p>
                  )}
                  <ul className={`space-y-5 ${group.subtitle ? "" : "mt-4"}`}>
                    {group.items.map((it, j) => (
                      <li key={j} data-testid={`cluster-en-${i}-item-${j}`}>
                        <p className="text-sm sm:text-[15px] text-slate-800 font-semibold tracking-[0.08em] uppercase leading-snug">
                          {it.title}
                        </p>
                        <p className="text-base sm:text-[17px] text-slate-600 leading-relaxed font-light mt-1.5">
                          {it.desc}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== NORMATIVE COMPARISONS ===== */}
      <section className="border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 py-16 sm:py-20">
          <p className="font-display text-xl sm:text-2xl md:text-3xl text-slate-600 italic font-light mb-4 text-center">
            And your data — compared to what?
          </p>
          <p className="text-base sm:text-lg text-slate-600 text-center max-w-2xl mx-auto mb-10 font-light leading-relaxed">
            Three reference layers to make sense of your numbers, while always respecting your privacy.
          </p>

          <div className="bg-white rounded-lg p-6 sm:p-7 box-gold box-gold-accent" data-testid="cluster-normativi-en">
            <p className="text-xs sm:text-sm text-gold font-medium tracking-[0.18em] uppercase mb-4">
              {normativi.cluster}
            </p>
            <ul className="space-y-5">
              {normativi.items.map((it, j) => (
                <li key={j} data-testid={`normativi-en-item-${j}`}>
                  <p className="text-sm sm:text-[15px] text-slate-800 font-semibold tracking-[0.08em] uppercase leading-snug">
                    {it.title}
                  </p>
                  <p className="text-base sm:text-[17px] text-slate-600 leading-relaxed font-light mt-1.5">
                    {it.desc}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ===== THE MORE YOU GIVE ===== */}
      <section className="border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 py-16 sm:py-20">
          <div className="bg-gradient-to-br from-slate-100 to-slate-50 border border-slate-200 rounded-lg p-8 sm:p-10" data-testid="more-data-block-en">
            <p className="font-display text-3xl sm:text-4xl md:text-5xl text-gold font-light leading-tight mb-5">
              The more you give, the more Revela gives back.
            </p>
            <p className="text-base sm:text-lg text-slate-700 leading-relaxed font-light mb-3">
              The more check-ins you log, the better the system understands you. First insights surface within days. After a few weeks the patterns emerge. After a few months Revela starts delivering <strong className="font-medium">increasingly precise readings</strong> of how you function and <strong className="font-medium">suggestions truly calibrated to you</strong> — not generic platitudes.
            </p>
            <p className="text-base text-slate-500 italic font-light">
              It's an honest deal: we ask for 25 seconds at a time, and together we build a faithful mirror of your wellbeing.
            </p>
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 py-16 sm:py-20">
          <p className="font-display text-xl sm:text-2xl md:text-3xl text-slate-600 italic font-light mb-10 text-center">
            How it works
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {steps.map((step, i) => (
              <div
                key={i}
                className="bg-white rounded-lg p-6 sm:p-7 box-gold box-gold-accent hover:shadow-md transition-shadow flex flex-col"
                data-testid={`step-en-${i}`}
              >
                <div className="flex items-baseline gap-3 mb-3">
                  <span className="font-display text-5xl text-gold font-light leading-none">
                    {step.n}
                  </span>
                  <span className="text-xs sm:text-sm text-gold font-medium tracking-[0.18em] uppercase">
                    Step {step.n}
                  </span>
                </div>
                <h4 className="font-display text-2xl sm:text-[26px] text-slate-800 font-light leading-tight mb-2">
                  {step.title}
                </h4>
                <p className="text-base sm:text-[17px] text-slate-600 leading-relaxed font-light">
                  {step.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PRIVACY ===== */}
      <section className="border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 py-16 sm:py-20 text-center" data-testid="privacy-promise-en">
          <p className="text-xs sm:text-sm text-gold font-medium tracking-[0.18em] uppercase mb-4">
            Privacy
          </p>
          <p className="font-display text-3xl sm:text-4xl md:text-5xl text-gold font-light leading-tight mb-5">
            Your data, only yours.
          </p>
          <p className="text-base sm:text-lg text-slate-600 leading-relaxed font-light max-w-2xl mx-auto mb-8">
            Your check-ins are never shared with third parties. They may only be used in fully anonymous, aggregated form — following the <strong className="font-medium">k-anonymity (k=5)</strong> standard — to improve the app. Revela is designed from the ground up around the principles of the <strong className="font-medium">EU GDPR</strong> and <em>privacy by design</em>.
          </p>
          <a
            href="/privacy"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-amber-700/40 text-slate-700 text-sm font-light tracking-wide hover:bg-amber-50/50 hover:border-amber-700/60 transition-colors"
            data-testid="privacy-cta-en"
          >
            How we handle your data
          </a>
        </div>
      </section>

      {/* ===== ABOUT THE FOUNDER + FRAMEWORKS ===== */}
      <section className="border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 py-16 sm:py-20" data-testid="about-author-en">
          <p className="font-display text-xl sm:text-2xl md:text-3xl text-slate-600 italic font-light mb-6 text-center">
            About the founder
          </p>
          <p className="text-base sm:text-lg text-slate-700 leading-relaxed font-light mb-8">
            <strong className="font-medium">Revela is a project by Laura Piccinini</strong>, psychologist and psychotherapist based in Italy. She designed every indicator, validation rule and interpretive layer in this platform.{" "}
            <a
              href="https://www.laurapiccinini.it"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-600 hover:text-slate-800 underline decoration-slate-300 underline-offset-2"
            >
              laurapiccinini.it
            </a>
          </p>

          <div className="bg-white rounded-lg p-6 sm:p-7 box-gold box-gold-accent" data-testid="frameworks-block-en">
            <p className="text-xs sm:text-sm text-gold font-medium tracking-[0.18em] uppercase mb-4">
              Built on peer-reviewed psychological frameworks
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
              {frameworks.map((fw, i) => (
                <li key={i} className="flex items-start gap-2 text-base text-slate-700 leading-relaxed font-light">
                  <span className="w-1 h-1 rounded-full bg-amber-700/40 mt-2.5 shrink-0" />
                  <span>{fw}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-slate-500 italic font-light mt-5 pt-4 border-t border-slate-100">
              All applied through the <strong className="font-medium not-italic text-slate-600">Ecological Momentary Assessment</strong> methodology (Trull &amp; Ebner-Priemer, 2013), the international gold standard for studying experience in its real-world context.
            </p>
          </div>
        </div>
      </section>

      {/* ===== BETA NOTE ===== */}
      <section className="border-b border-slate-200 bg-slate-50">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 py-12 sm:py-16" data-testid="beta-note-en">
          <p className="font-display text-3xl sm:text-4xl text-slate-700 font-light leading-tight mb-4">
            An early version, crafted with care.
          </p>
          <p className="text-base sm:text-lg text-slate-600 leading-relaxed font-light">
            Revela is currently in <strong className="font-medium">private beta</strong>: we're tuning interpretations, thresholds and personalized readings together with the first people using it. You may spot small details still to polish — but your experience is genuinely shaping the platform. Free of charge for now.
          </p>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section>
        <div className="max-w-3xl mx-auto px-6 sm:px-8 py-20 sm:py-24 text-center">
          <p className="font-display text-3xl sm:text-4xl md:text-5xl text-slate-800 font-light leading-tight mb-8">
            Ready to discover your patterns?
          </p>
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-center">
            <button
              onClick={openBetaForm}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 text-white text-base font-light tracking-wide hover:shadow-lg transition-shadow"
              data-testid="final-cta-beta-tester-en"
            >
              Request beta access
            </button>
            <button
              onClick={openPartnerForm}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-amber-700/40 text-slate-700 text-base font-light tracking-wide hover:bg-amber-50/50 hover:border-amber-700/60 transition-colors"
              data-testid="final-cta-partner-en"
            >
              Join as a scientific partner
            </button>
          </div>
        </div>
      </section>

      {/* ===== NON-CLINICAL DISCLAIMER ===== */}
      <section className="bg-white border-t border-slate-100">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 py-8 text-center" data-testid="non-clinical-disclaimer-en">
          <p className="text-xs sm:text-sm text-slate-500 italic font-light leading-relaxed">
            Revela does not provide clinical assessments and is in no way a substitute for professional consultation. It's a tool designed to help you observe subjective patterns in your daily experiences. The suggestions you receive are prompts for reflection, always to be evaluated with your own judgement.
          </p>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 py-6 text-center">
          <p className="text-[11px] text-slate-400 font-light tracking-wide">
            © {new Date().getFullYear()} Revela · Laura Piccinini ·{" "}
            <a href="/privacy" className="hover:text-slate-600 underline decoration-slate-200 underline-offset-2">Privacy</a>
            {" · "}
            <a href="/termini" className="hover:text-slate-600 underline decoration-slate-200 underline-offset-2">Terms</a>
          </p>
        </div>
      </footer>

      {/* ===== REQUEST FORM MODAL ===== */}
      {requestType && (
        <RequestModalEN type={requestType} onClose={closeForm} />
      )}
    </div>
  );
};

/**
 * Small fixed-position bar in the top-right corner:
 *   [Accedi / Sign in]  [IT | EN]
 * Visible on both landing pages (IT and EN). Uses `fixed` so it stays
 * visible while the user scrolls the very long landing page.
 */
export const LanguageToggle = ({ current = "it" }) => {
  const loginLabel = current === "en" ? "Sign in" : "Accedi";
  return (
    <div className="fixed top-4 right-4 sm:top-6 sm:right-6 z-30 flex items-center gap-2" data-testid="landing-header-bar">
      <Link
        to="/login"
        className="px-4 py-2 rounded-full bg-white border border-slate-300 text-xs sm:text-sm font-medium tracking-wide shadow-md text-slate-800 hover:bg-slate-50 hover:border-slate-400 hover:shadow-lg transition-all"
        data-testid="landing-login-link"
      >
        {loginLabel}
      </Link>
      <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-full px-1 py-1 text-xs font-medium tracking-wide shadow-sm" data-testid="language-toggle">
        <Link
          to="/"
          className={`px-3 py-1 rounded-full transition-colors ${current === "it" ? "bg-slate-800 text-white" : "text-slate-500 hover:text-slate-800"}`}
          data-testid="language-toggle-it"
        >
          IT
        </Link>
        <Link
          to="/en"
          className={`px-3 py-1 rounded-full transition-colors ${current === "en" ? "bg-slate-800 text-white" : "text-slate-500 hover:text-slate-800"}`}
          data-testid="language-toggle-en"
        >
          EN
        </Link>
      </div>
    </div>
  );
};

/**
 * RequestModalEN — English version of the contact form.
 * Same backend endpoint: POST /api/landing/contact-request
 */
const RequestModalEN = ({ type, onClose }) => {
  const isBeta = type === "beta";
  const title = isBeta ? "Request beta access" : "Join as a scientific partner";
  const intro = isBeta
    ? "Leave us your details and we'll get back to you to activate your beta access. You'll start logging check-ins immediately and within a few days you'll see your first behavioral patterns."
    : "Leave your details and a short note: we'll get in touch to explore a possible scientific collaboration (research, validation, academic partnership).";

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim() || !email.trim()) {
      setError("Name and email are required.");
      return;
    }
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(email.trim())) {
      setError("That email address doesn't look quite right.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/landing/contact-request", {
        type,
        name: name.trim(),
        email: email.trim(),
        message: message.trim(),
      });
      setSubmitted(true);
    } catch (err) {
      setError("Something didn't work. Please try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm px-4 py-8"
      onClick={onClose}
      data-testid="request-modal-backdrop-en"
    >
      <div
        className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid={`request-modal-en-${type}`}
      >
        <div className="flex items-start justify-between p-6 sm:p-8 border-b border-slate-100">
          <div>
            <p className="text-xs text-gold font-medium tracking-[0.18em] uppercase mb-1">
              {isBeta ? "Beta tester" : "Scientific partner"}
            </p>
            <h3 className="font-display text-2xl sm:text-3xl text-slate-800 font-light leading-tight">
              {title}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-slate-100 transition-colors -mr-2 -mt-2"
            data-testid="request-modal-close-en"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-slate-500" strokeWidth={1.5} />
          </button>
        </div>

        {submitted ? (
          <div className="p-8 text-center" data-testid="request-modal-success-en">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" strokeWidth={1.5} />
            <p className="font-display text-xl text-slate-700 mb-3">Request received. Thank you.</p>
            <p className="text-base text-slate-600 leading-relaxed font-light mb-6">
              We'll get back to you personally at {email} in the next few days.
            </p>
            <button
              onClick={onClose}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 text-white text-sm font-light tracking-wide hover:shadow-lg transition-shadow"
              data-testid="request-modal-close-success-en"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate className="p-6 sm:p-8 space-y-5">
            <p className="text-base text-slate-600 leading-relaxed font-light">
              {intro}
            </p>

            <div>
              <label className="block text-xs text-slate-500 font-medium tracking-[0.12em] uppercase mb-2">
                Full name *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-3 border border-slate-200 rounded-lg text-slate-800 font-light focus:outline-none focus:border-amber-700/60 focus:ring-1 focus:ring-amber-700/30 transition-colors"
                placeholder="e.g. Mary Johnson"
                data-testid="request-modal-name-en"
                disabled={submitting}
              />
            </div>

            <div>
              <label className="block text-xs text-slate-500 font-medium tracking-[0.12em] uppercase mb-2">
                Email *
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-slate-200 rounded-lg text-slate-800 font-light focus:outline-none focus:border-amber-700/60 focus:ring-1 focus:ring-amber-700/30 transition-colors"
                placeholder="your-email@example.com"
                data-testid="request-modal-email-en"
                disabled={submitting}
              />
            </div>

            <div>
              <label className="block text-xs text-slate-500 font-medium tracking-[0.12em] uppercase mb-2">
                {isBeta ? "Anything you'd like to share? (optional)" : "How would you like to collaborate? (optional)"}
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                className="w-full px-4 py-3 border border-slate-200 rounded-lg text-slate-800 font-light focus:outline-none focus:border-amber-700/60 focus:ring-1 focus:ring-amber-700/30 transition-colors resize-none"
                placeholder={isBeta
                  ? "e.g. I'm a psychologist interested in wellbeing patterns..."
                  : "e.g. I'm a researcher at... working on EMA and affective regulation..."}
                data-testid="request-modal-message-en"
                disabled={submitting}
              />
            </div>

            {error && (
              <p className="text-sm text-red-600 font-light" data-testid="request-modal-error-en">
                {error}
              </p>
            )}

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 text-white text-sm font-light tracking-wide hover:shadow-lg transition-shadow disabled:opacity-60 disabled:cursor-not-allowed flex-1"
                data-testid="request-modal-submit-en"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> Sending...</>
                ) : (
                  "Send request"
                )}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-6 py-3 rounded-full border border-slate-200 text-slate-600 text-sm font-light tracking-wide hover:bg-slate-50 transition-colors disabled:opacity-60"
                data-testid="request-modal-cancel-en"
              >
                Cancel
              </button>
            </div>

            <p className="text-xs text-slate-400 italic font-light pt-2">
              Your data will only be used to get back to you, in full compliance with the EU GDPR.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default LandingPageEN;
